// src/components/ComingSoon.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  MessageCircle, Users, Sparkles, Heart,
  ArrowRight, CheckCircle,
  BookOpen, Trophy, Mail, Loader2
} from 'lucide-react';
import { logEvent } from 'firebase/analytics';
import { httpsCallable } from 'firebase/functions';
import { analytics, functions } from '../config/firebase';

const ComingSoon = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [interest, setInterest] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      // The waitlist collection is closed to clients (its docs hold names
      // and emails), so signup goes through the joinWaitlist callable: it
      // validates, enforces duplicates and handles the DDD Email Hub
      // subscription server-side, keeping the hub API key out of the bundle.
      const joinWaitlist = httpsCallable(functions, 'joinWaitlist');
      await joinWaitlist({
        email: email.toLowerCase(),
        name: name.trim(),
        interest: interest,
      });

      // Track waitlist signup in analytics
      if (analytics) {
        logEvent(analytics, 'waitlist_signup', {
          interest: interest || 'not_specified',
          source: 'splash-coming-soon'
        });
      }

      setStatus('success');
      setEmail('');
      setName('');
      setInterest('');
    } catch (error) {
      if (error?.code === 'functions/already-exists') {
        setStatus('error');
        setErrorMessage('This email is already on the waitlist!');
        return;
      }
      if (error?.code === 'functions/invalid-argument') {
        setStatus('error');
        setErrorMessage('Please enter a valid email address');
        return;
      }
      console.error('Waitlist signup error:', error);
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  // Ordered by how real each one is. The Legacy Pool is built and working; the rest
  // are honestly labelled as intentions. See apps/splash/docs/REPOSITIONING_2026.md.
  const features = [
    {
      icon: BookOpen,
      title: 'The Legacy Pool',
      status: 'Built, opening first',
      description: 'Families post the mysteries in their own history — an unexplained photograph, a regiment nobody can place — and the community helps investigate.'
    },
    {
      icon: Heart,
      title: 'The First Contribution Pool',
      status: 'Not open yet',
      description: "Small, real needs from people and local organisations that a young person's weekly challenge could serve. Always through their parent, never directly."
    },
    {
      icon: Users,
      title: 'The guild hall for guides',
      status: 'Not open yet',
      description: 'Parents who finish a season are best placed to walk alongside the next family. This is where that craft will be learned and supported.'
    },
    {
      icon: MessageCircle,
      title: 'A place for the people around it',
      status: 'Quiet on purpose',
      description: "Somewhere to compare notes without a feed to feed. We'd rather have one useful conversation than a wall of activity nobody reads."
    }
  ];

  const interests = [
    { value: 'walking-a-season', label: "I'd like to walk a season with my young person" },
    { value: 'legacy-pool', label: 'I could help investigate family mysteries' },
    { value: 'contribution-pool', label: 'I have small real needs a young person could help with' },
    { value: 'guiding', label: 'I might want to guide other families one day' },
    { value: 'watching', label: "I'm interested — just watching for now" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f0f23] text-white">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="py-6 px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/splash-logo-v2-150px.png"
                alt="SPLASH!"
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold">SPLASH!</h1>
                <p className="text-xs text-white/60">by Dive Deep Dialogues</p>
              </div>
            </div>
            <a
              href="https://divedeepdialogues.com"
              className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
            >
              Visit Dive Deep Dialogues →
            </a>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Main Logo */}
              <motion.img
                src="/splash-logo-v2-350px.png"
                alt="SPLASH!"
                className="w-40 h-40 md:w-56 md:h-56 mx-auto mb-8 object-contain drop-shadow-2xl"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              />

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-full mb-8">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-purple-300">Coming Soon</span>
              </div>

              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                The
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-violet-400 to-indigo-400">
                  clubhouse
                </span>
                for a community experiment
              </h1>

              <p className="text-xl md:text-2xl text-white/70 mb-8 max-w-3xl mx-auto leading-relaxed">
                SPLASH! is the community around <strong className="text-white">The VIBE Experiment</strong> —
                where the wider community helps the mission along, and where parents who
                walk a season will one day train as guides for other families.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-white/60">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Pools, not forums</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>No gurus, no gatekeepers</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Built at the pace it earns</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* The mission — the reason SPLASH! exists at all */}
        <section className="pb-4 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12"
            >
              <p className="text-sm font-medium uppercase tracking-wide text-purple-300 mb-3">
                What SPLASH! exists to support
              </p>

              <h2 className="text-3xl md:text-4xl font-bold mb-3">
                The <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400">VIBE</span> Experiment
              </h2>

              <p className="text-xl text-white/90 font-semibold mb-6">
                Ten weeks. Four pillars. Side by side. For whatever’s ahead.
              </p>

              <p className="text-white/70 leading-relaxed mb-4">
                Nobody can tell a young adult what they’ll be any more. This is ten
                weeks of building what they’ll take with them: purpose, identity,
                belonging, contribution. Young adults and the adults walking with them
                take the season together — one conversation a week, shoulder to shoulder
                on a walk or a drive, and one small real-world challenge that the adults
                do a version of too.
              </p>

              <p className="text-white/70 leading-relaxed mb-4">
                It's family practice, not therapy. The research underneath it is solid;
                the practice itself is genuinely an experiment, which is why it needs
                families willing to walk it and say honestly what worked.
              </p>

              <p className="text-white/60 leading-relaxed mb-8">
                A season doesn't run inside SPLASH! — it runs on email, a form and video
                calls, deliberately. The whole plan is public, so you can see exactly
                what's involved before you decide anything.
              </p>

              <a
                href="https://divedeepdialogues.com/vibe-experiment"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white font-semibold rounded-xl transition-all duration-300"
              >
                Read the plan
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Signup Section */}
        <section className="py-16 px-4">
          <div className="max-w-xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl mb-4">
                    <Trophy className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Tell us you're interested</h2>
                  <p className="text-white/60">
                    We'll let you know when there's something real to join. If you want to
                    walk a VIBE Experiment season, the plan is already public — start there.
                  </p>
                </div>

                {status === 'success' ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Thanks — we've got you.</h3>
                    <p className="text-white/60 mb-6">
                      We'll be in touch when there's something worth joining, and not before.
                    </p>
                    <a
                      href="https://divedeepdialogues.com/vibe-experiment"
                      className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      Read The VIBE Experiment plan
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Your name
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter your name"
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Email address *
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Which of these sounds most like you?
                      </label>
                      <select
                        value={interest}
                        onChange={(e) => setInterest(e.target.value)}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      >
                        <option value="" className="bg-gray-800">Select an option</option>
                        {interests.map((opt) => (
                          <option key={opt.value} value={opt.value} className="bg-gray-800">
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {status === 'error' && (
                      <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300 text-sm">
                        {errorMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="w-full py-4 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {status === 'loading' ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="w-5 h-5" />
                          Keep me posted
                        </>
                      )}
                    </button>

                    <p className="text-center text-xs text-white/40">
                      No spam, no upsells. We'll only email you about SPLASH! and The VIBE Experiment.
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                What the <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-400">clubhouse</span> will hold
              </h2>
              <p className="text-lg text-white/60 max-w-2xl mx-auto">
                Pools, not forums. Each of these opens when it has earned its place — and
                we'll say plainly which ones haven't yet.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-violet-500/20 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-purple-400" />
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                    <h3 className="text-xl font-bold">{feature.title}</h3>
                    <span className="text-xs uppercase tracking-wide text-white/40">
                      {feature.status}
                    </span>
                  </div>
                  <p className="text-white/60">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center text-white/50 max-w-2xl mx-auto mt-12 leading-relaxed"
            >
              We're at the beginning. There are no thousands of members and no bustling
              chat rooms — and we'd rather tell you that than build rooms nobody asked for.
              What SPLASH! becomes depends on what the first season actually teaches us.
            </motion.p>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img
                src="/splash-logo-v2-150px.png"
                alt="SPLASH!"
                className="w-8 h-8 object-contain"
              />
              <span className="font-bold">SPLASH!</span>
              <span className="text-white/40">by</span>
              <a href="https://divedeepdialogues.com" className="text-purple-400 hover:text-purple-300">
                Dive Deep Dialogues
              </a>
            </div>
            <p className="text-sm text-white/40">
              © {new Date().getFullYear()} SILVER WOLF Productions. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ComingSoon;
